/**
 * SalesPageBuilder Modal
 * 
 * Generates sales pages based on user's business with automatic business type detection.
 * Output varies by plan tier:
 * - Starter: Section copy + DIY instructions
 * - Growth: Editable template format  
 * - Pro: Done-for-you finished sales page ready to download
 */
import { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, MagicWand, Sparkle, Lightning, CheckCircle, Copy, Download, 
  ArrowRight, FileText, Browser, Code, CurrencyDollar,
  Spinner, Info, Eye, PencilSimple, Question, ArrowUpRight, Lock, ArrowDown
} from '@phosphor-icons/react';
import DOMPurify from 'dompurify';
import { Link } from 'react-router-dom';
import { isTrialUser, canDownload, getUpgradeMessage, getEffectiveTier } from '@/utils/trialAccess';
import { toast } from 'sonner';
import StyledHtmlPreview from './StyledHtmlPreview';
import RegenerateWithTweaks from './RegenerateWithTweaks';
import api from '@/utils/api';
import { downloadBlob as safeDownloadBlob } from '@/utils/downloadBlob';
import useBusinessMemoryPrefill from '@/hooks/useBusinessMemoryPrefill';
import useFoundationAutoStart from '@/hooks/useFoundationAutoStart';
import { useMissionHandoff } from '@/store/missionHandoffContext';
import { normalizeBuilderAsset } from '@/utils/normalizeBuilderAsset';
import BusinessMemoryBanner from './BusinessMemoryBanner';
import useFoundationAutoRoute from '@/hooks/useFoundationAutoRoute';
import NextStepGuide from './NextStepGuide';
import EditablePreview from './EditablePreview';

const TIER_BADGES = {
  starter: { label: 'Starter', color: 'bg-slate-600', description: 'Copy + DIY Instructions' },
  growth: { label: 'Growth', color: 'bg-blue-600', description: 'Editable Template' },
  pro: { label: 'Pro', color: 'bg-purple-600', description: 'Done-For-You + PDF' }
};

export default function SalesPageBuilder({ isOpen, onClose, onAction, onPickStep, onSuccess, user }) {
  const [step, setStep] = useState('input'); // 'input', 'generating', 'result'
  const [businessDescription, setBusinessDescription] = useState('');
  const [targetCustomer, setTargetCustomer] = useState('');
  const [offer, setOffer] = useState('');
  const [price, setPrice] = useState('');
  const [cta, setCta] = useState('Get Started Now');
  const [detectedBusiness, setDetectedBusiness] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [activeSection, setActiveSection] = useState(null);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');

  // Round 108 — mission handoff prefill.
  const { handoff: missionHandoff, pickField: pickMissionField } = useMissionHandoff();
  const isMissionMode = !!(missionHandoff && missionHandoff.asset_id
    && (missionHandoff.asset_type === 'sales_page' || !missionHandoff.asset_type));

  useEffect(() => {
    if (!isOpen || !isMissionMode) return;
    const missionDesc = pickMissionField('body', 'summary', 'pitch');
    if (missionDesc && typeof missionDesc === 'string') setBusinessDescription(missionDesc);
    const missionCustomer = pickMissionField('recommended_audience', 'target_audience');
    if (missionCustomer && typeof missionCustomer === 'string') setTargetCustomer(missionCustomer);
    const missionOffer = pickMissionField('headline', 'pitch', 'title', 'subheadline');
    if (missionOffer && typeof missionOffer === 'string') setOffer(missionOffer);
    const missionPrice = pickMissionField('price');
    if (missionPrice && typeof missionPrice === 'string') setPrice(missionPrice);
    const missionCta = pickMissionField('cta', 'cta_copy');
    if (missionCta && typeof missionCta === 'string') setCta(missionCta);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isMissionMode]);

  // Determine effective plan tier (uses simulation-aware function)
  const effectiveTier = getEffectiveTier(user);
  const tierInfo = TIER_BADGES[effectiveTier] || TIER_BADGES.starter;
  
  // Check if user is on trial (simulation-aware)
  const isTrial = isTrialUser(user);
  const canUserDownload = canDownload(user);

  // Canonical business memory prefill (hydrates from Business Builder, Business
  // Plan, Offers — user types only override explicitly)
  useBusinessMemoryPrefill(isOpen, {
    businessDescription: [businessDescription, setBusinessDescription],
    targetCustomer: [targetCustomer, setTargetCustomer],
    offer: [offer, setOffer],
    price: [price, setPrice],
  });

  // Round 218A P0 FINAL (2026-02-07) — auto-route.
  // If the customer opens this builder before the foundation is
  // complete, we silently close the modal, toast the next step, and
  // navigate to Brand Builder (or whatever the resolver names as
  // next). The customer never sees a blocker in the normal flow.
  useFoundationAutoRoute({ isOpen, onClose });

  // Round 218A Phase 8D-3D — auto-consume Foundation when complete.
  useFoundationAutoStart({
    isOpen,
    ready: !!(businessDescription && targetCustomer && offer),
    step,
    onStart: () => handleGenerate(),
  });

  // Auto-detect business type as user types
  useEffect(() => {
    const detectTimeout = setTimeout(async () => {
      const combined = `${businessDescription} ${offer} ${targetCustomer}`.trim();
      if (combined.length >= 10) {
        try {
          const response = await api.post('/sales-page-builder/detect-business', {
            business_description: businessDescription,
            offer: offer,
            target_customer: targetCustomer
          });
          setDetectedBusiness(response.data);
        } catch (error) {
          // Silent fail for preview
        }
      }
    }, 500);

    return () => clearTimeout(detectTimeout);
  }, [businessDescription, offer, targetCustomer]);

  const handleGenerate = async () => {
    if (!businessDescription.trim()) {
      toast.error('Please describe your business');
      return;
    }
    if (!offer.trim()) {
      toast.error('Please describe your offer');
      return;
    }
    if (!price.trim()) {
      toast.error('Please enter a price');
      return;
    }

    setLoading(true);
    setStep('generating');

    try {
      const response = await api.post('/sales-page-builder/generate', {
        business_description: businessDescription,
        target_customer: targetCustomer || 'ideal customers',
        offer: offer,
        price: price,
        cta: cta || 'Get Started Now'
      });

      setResult(response.data);
      setStep('result');
      toast.success('Sales page created!');
      try { if (onSuccess) onSuccess({ meta: normalizeBuilderAsset(response.data) }); } catch (_) { /* noop */ }
    } catch (error) {
      console.error('Generation failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate sales page');
      setStep('input');
    } finally {
      setLoading(false);
    }
  };

  // Regenerate with targeted tweaks — keeps modal open, replaces preview on success.
  const [regenerating, setRegenerating] = useState(false);
  const handleRegenerate = async (updatedFields) => {
    setRegenerating(true);
    try {
      const response = await api.post('/sales-page-builder/generate', {
        business_description: updatedFields.business_description || businessDescription,
        target_customer: updatedFields.target_customer || targetCustomer || 'ideal customers',
        offer: updatedFields.offer || offer,
        price: updatedFields.price || price,
        cta: updatedFields.cta || cta || 'Get Started Now',
        headline_tone: updatedFields.headline_tone || '',
        tweak_notes: updatedFields.tweak_notes || '',
      });
      setResult(response.data);
      // Sync the local form state so subsequent "Regenerate" reflects what's now shown
      if (updatedFields.offer) setOffer(updatedFields.offer);
      if (updatedFields.price) setPrice(updatedFields.price);
      if (updatedFields.cta) setCta(updatedFields.cta);
      if (updatedFields.target_customer) setTargetCustomer(updatedFields.target_customer);
      toast.success('Sales page updated with your tweaks!');
    } catch (error) {
      console.error('Regeneration failed:', error);
      toast.error(error.response?.data?.detail || "Couldn't regenerate — your current preview is preserved.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleCopy = (text) => {
    // Block copy for trial users
    if (isTrial) {
      setUpgradeMessage('Upgrade to copy full content. Trial users get preview only.');
      setShowUpgradePrompt(true);
      return;
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadHTML = async () => {
    /* Phase G — V2 grants export to every subscriber */
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_html'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.formatted_output?.html_version) return;

    const blob = new Blob([result.formatted_output.html_version], { type: 'text/html' });
    await safeDownloadBlob(blob, `${result.headline?.replace(/[^a-z0-9]/gi, '_') || 'sales_page'}.html`);
    toast.success('Downloaded HTML!');
  };

  const handleDownloadPDF = async () => {
    /* Phase G — V2 grants export to every subscriber */
    // Check trial access
    if (!canUserDownload) {
      setUpgradeMessage(getUpgradeMessage('download_pdf'));
      setShowUpgradePrompt(true);
      return;
    }
    
    if (!result?.id || !result?.has_pdf) {
      toast.error('PDF not available');
      return;
    }

    try {
      const response = await api.get(`/sales-page-builder/${result.id}/download`, {
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      await safeDownloadBlob(blob, `${result.headline?.replace(/[^a-z0-9]/gi, '_') || 'sales_page'}.pdf`);
      toast.success('PDF Downloaded!');
    } catch (error) {
      console.error('PDF download failed:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleReset = () => {
    setStep('input');
    setBusinessDescription('');
    setTargetCustomer('');
    setOffer('');
    setPrice('');
    setCta('Get Started Now');
    setDetectedBusiness(null);
    setResult(null);
    setActiveSection(null);
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // ──────────────────────────────────────────────────────────
  // Universal Editable Preview Mode (Phase 2) — Sales Page
  //
  // PATCH /api/sales-page-builder/{id}. Server whitelist drops
  // anything outside SALES_PAGE_EDITABLE_FIELDS. business_memory
  // is NEVER touched.
  // ──────────────────────────────────────────────────────────
  const [savingSalesPageEdits, setSavingSalesPageEdits] = useState(false);
  const editableSalesPage = useMemo(() => ({
    headline: result?.headline || '',
    subheadline: result?.subheadline || '',
    final_cta: result?.final_cta || '',
    cta_button_text: result?.cta_button_text || '',
    guarantee: result?.guarantee || '',
  }), [result]);

  const handleSaveSalesPageEdits = useCallback(async (next) => {
    if (!result?.id) {
      toast.error('Sales page ID missing — please regenerate.');
      return;
    }
    setSavingSalesPageEdits(true);
    try {
      const body = {
        headline: next.headline,
        subheadline: next.subheadline,
        final_cta: next.final_cta,
        cta_button_text: next.cta_button_text,
        guarantee: next.guarantee,
      };
      const { data } = await api.patch(`/sales-page-builder/${result.id}`, body, { timeout: 30000 });
      setResult((prev) => (prev ? { ...prev, ...body, last_edited_at: data?.saved_at } : prev));
    } finally {
      setSavingSalesPageEdits(false);
    }
  }, [result?.id]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={handleClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden border border-white/10 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          data-testid="sales-page-builder-modal"
        >
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center">
                <Browser size={20} weight="fill" className="text-white" />
              </div>
              <div>
                <h2 className="text-white text-lg font-bold">Sales Page Builder</h2>
                <div className="flex items-center space-x-2">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${tierInfo.color} text-white`}>
                    {tierInfo.label}
                  </span>
                  <span className="text-slate-400 text-xs">{tierInfo.description}</span>
                </div>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
              data-testid="close-sales-page-modal"
            >
              <X size={20} className="text-slate-400" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            <AnimatePresence mode="wait">
              {/* INPUT STEP */}
              {step === 'input' && (
                <motion.div
                  key="input"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: 20 }}
                  className="space-y-4"
                >
                  {/* Round 218A P0 FINAL — this builder only opens
                      once foundation is complete (useFoundationAutoRoute
                      silently redirects otherwise). Business
                      Description / Target Customer / Offer / Price
                      / CTA all come from the active foundation. No
                      duplicate intake. Values are still prefilled
                      into hidden state via useBusinessMemoryPrefill
                      so the generate payload carries them. */}

                  {/* Target Customer */}
                  {/* Target Customer — always hidden; loaded from
                      active Business Brain via prefill. */}
                  {false && (
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      Who is your ideal customer?
                    </label>
                    <input
                      type="text"
                      value={targetCustomer}
                      onChange={(e) => setTargetCustomer(e.target.value)}
                      placeholder="e.g., Busy professionals aged 30-50 who want to lose weight"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                      data-testid="target-customer-input"
                    />
                  </div>
                  )}

                  {/* Offer — always hidden; loaded from the ONE
                      approved offer in Business Brain. */}
                  {false && (
                  <div>
                    <label className="block text-white text-sm font-medium mb-2">
                      What are you selling? <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={offer}
                      onChange={(e) => setOffer(e.target.value)}
                      placeholder="e.g., 12-Week Fitness Transformation Program"
                      className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                      data-testid="offer-input"
                    />
                  </div>
                  )}

                  {/* Price + CTA — always hidden; loaded from
                      active foundation. */}
                  {false && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Price <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="text"
                        value={price}
                        onChange={(e) => setPrice(e.target.value)}
                        placeholder="e.g., $297"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                        data-testid="price-input"
                      />
                    </div>
                    <div>
                      <label className="block text-white text-sm font-medium mb-2">
                        Call to Action
                      </label>
                      <input
                        type="text"
                        value={cta}
                        onChange={(e) => setCta(e.target.value)}
                        placeholder="e.g., Start Now"
                        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500/50"
                        data-testid="cta-input"
                      />
                    </div>
                  </div>
                  )}

                  {/* Tier Preview */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <Info size={16} className="text-blue-400" />
                      <span className="text-white text-sm font-medium">What you'll get ({tierInfo.label})</span>
                    </div>
                    {effectiveTier === 'starter' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Complete copy for all sections</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Step-by-step DIY instructions</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Free tool recommendations (Carrd, Google Sites)</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'growth' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Editable template with [customizable] sections</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Multiple headline variations</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>CTA options to choose from</span>
                        </li>
                      </ul>
                    )}
                    {effectiveTier === 'pro' && (
                      <ul className="space-y-2 text-sm text-slate-300">
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Finished, professional sales page</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Download as HTML or PDF</span>
                        </li>
                        <li className="flex items-center space-x-2">
                          <CheckCircle size={14} className="text-emerald-400" weight="fill" />
                          <span>Ready to use immediately</span>
                        </li>
                      </ul>
                    )}
                  </div>

                  {/* Generate Button — foundation completeness is
                      enforced by useFoundationAutoRoute (silent
                      redirect if incomplete). */}
                  <button
                    onClick={handleGenerate}
                    disabled={!businessDescription.trim() || !offer.trim() || !price.trim() || loading}
                    className="w-full py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                    data-testid="generate-sales-page-btn"
                  >
                    <Browser size={20} weight="fill" />
                    <span>Generate My Sales Page</span>
                    <ArrowRight size={18} weight="bold" />
                  </button>
                </motion.div>
              )}

              {/* GENERATING STEP */}
              {step === 'generating' && (
                <motion.div
                  key="generating"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="py-12 text-center"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Spinner size={32} className="text-white animate-spin" />
                  </div>
                  <h3 className="text-white text-lg font-bold mb-2">Creating Your Sales Page</h3>
                  <p className="text-slate-400 text-sm">
                    Crafting high-converting copy for your offer...
                  </p>
                  <div className="mt-6 space-y-2 max-w-xs mx-auto">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 4 }}
                      className="h-1 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full"
                    />
                  </div>
                </motion.div>
              )}

              {/* RESULT STEP */}
              {step === 'result' && result && (
                <motion.div
                  key="result"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-5"
                >
                  {/* Success Header */}
                  <div className="bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border border-emerald-500/30 rounded-xl p-4">
                    <div className="flex items-center space-x-3">
                      <CheckCircle size={24} className="text-emerald-400" weight="fill" />
                      <div>
                        <h3 className="text-white font-bold">Sales Page Created!</h3>
                        <p className="text-emerald-400 text-sm">Saved to My Creations</p>
                      </div>
                    </div>
                  </div>

                  {/* Headline & Info */}
                  <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                    <h4 className="text-white text-lg font-bold mb-1">{result.headline}</h4>
                    {result.subheadline && (
                      <p className="text-slate-400 text-sm">{result.subheadline}</p>
                    )}
                    <div className="flex items-center space-x-3 mt-3 text-xs">
                      <span className="text-slate-400">Business: <span className="text-blue-400">{result.detected_business_name}</span></span>
                      <span className="text-slate-400">Tier: <span className="text-purple-400">{result.plan_tier}</span></span>
                    </div>
                  </div>

                  {/* Universal Editable Preview Mode — adjust headline /
                      subheadline / final CTA / CTA button / guarantee
                      before downloading. business_memory NEVER mutated. */}
                  {result.id && (
                    <EditablePreview
                      draftKey={`sales-page-draft-${result.id}`}
                      originalValue={editableSalesPage}
                      onSave={handleSaveSalesPageEdits}
                      busy={savingSalesPageEdits}
                      savedAt={result.last_edited_at || null}
                      previewSlot={({ value }) => (
                        <div className="space-y-2 text-sm text-slate-200">
                          <div>
                            <p className="text-[11px] uppercase tracking-wide text-slate-500">Headline</p>
                            <p className="text-white font-semibold">{value.headline || '—'}</p>
                          </div>
                          {value.subheadline && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Sub-headline</p>
                              <p>{value.subheadline}</p>
                            </div>
                          )}
                          {value.final_cta && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Final CTA</p>
                              <p className="text-blue-300">{value.final_cta}</p>
                            </div>
                          )}
                          {value.cta_button_text && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">CTA button</p>
                              <p className="text-emerald-300">{value.cta_button_text}</p>
                            </div>
                          )}
                          {value.guarantee && (
                            <div>
                              <p className="text-[11px] uppercase tracking-wide text-slate-500">Guarantee</p>
                              <p>{value.guarantee}</p>
                            </div>
                          )}
                        </div>
                      )}
                      editSlot={({ value, setValue }) => (
                        <div className="space-y-3">
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Headline</span>
                            <input
                              type="text"
                              value={value.headline || ''}
                              onChange={(e) => setValue({ ...value, headline: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-400 focus:outline-none"
                              data-testid="sales-page-edit-headline"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Sub-headline</span>
                            <input
                              type="text"
                              value={value.subheadline || ''}
                              onChange={(e) => setValue({ ...value, subheadline: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-400 focus:outline-none"
                              data-testid="sales-page-edit-subheadline"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Final CTA</span>
                            <textarea
                              value={value.final_cta || ''}
                              onChange={(e) => setValue({ ...value, final_cta: e.target.value })}
                              rows={2}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-400 focus:outline-none"
                              data-testid="sales-page-edit-final-cta"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">CTA button</span>
                            <input
                              type="text"
                              value={value.cta_button_text || ''}
                              onChange={(e) => setValue({ ...value, cta_button_text: e.target.value })}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-400 focus:outline-none"
                              data-testid="sales-page-edit-cta-btn"
                            />
                          </label>
                          <label className="block">
                            <span className="text-[11px] uppercase tracking-wide text-slate-500">Guarantee</span>
                            <textarea
                              value={value.guarantee || ''}
                              onChange={(e) => setValue({ ...value, guarantee: e.target.value })}
                              rows={2}
                              className="mt-1 w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:border-blue-400 focus:outline-none"
                              data-testid="sales-page-edit-guarantee"
                            />
                          </label>
                        </div>
                      )}
                    />
                  )}

                  {/* Tier-Specific Output */}
                  {result.formatted_output && (
                    <div className="space-y-4">
                      {/* STARTER OUTPUT */}
                      {result.formatted_output.output_type === 'text_with_instructions' && (
                        <>
                          {/* Sections Copy */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-white font-medium flex items-center space-x-2">
                                <FileText size={16} className="text-blue-400" />
                                <span>Your Sales Page Copy</span>
                              </h5>
                              <button
                                onClick={() => handleCopy(result.formatted_output.plain_text || JSON.stringify(result.formatted_output.sections_copy, null, 2))}
                                className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                              >
                                <Copy size={12} />
                                <span>{copied ? 'Copied!' : 'Copy All'}</span>
                              </button>
                            </div>
                            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                              {result.formatted_output.plain_text || JSON.stringify(result.formatted_output.sections_copy, null, 2)}
                            </pre>
                          </div>

                          {/* DIY Instructions */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <h5 className="text-white font-medium mb-3 flex items-center space-x-2">
                              <Lightning size={16} className="text-amber-400" />
                              <span>How to Build It (DIY)</span>
                            </h5>
                            <ol className="space-y-2">
                              {(result.formatted_output.diy_instructions || []).map((instruction, i) => (
                                <li key={i} className="flex items-start space-x-3 text-sm">
                                  <span className="w-5 h-5 bg-amber-500/20 text-amber-400 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold">
                                    {i + 1}
                                  </span>
                                  <span className="text-slate-300">{instruction}</span>
                                </li>
                              ))}
                            </ol>
                          </div>

                          {/* Free Tools */}
                          {result.formatted_output.free_tools?.length > 0 && (
                            <div className="flex items-center space-x-2 text-sm text-slate-400">
                              <Lightning size={14} className="text-amber-400" />
                              <span>Recommended tools:</span>
                              <span className="text-white">{result.formatted_output.free_tools.join(', ')}</span>
                            </div>
                          )}
                        </>
                      )}

                      {/* GROWTH OUTPUT */}
                      {result.formatted_output.output_type === 'editable_template' && (
                        <>
                          {/* Regenerate with Tweaks */}
                          <RegenerateWithTweaks
                            fields={{
                              business_description: businessDescription,
                              target_customer: targetCustomer,
                              offer,
                              price,
                              cta,
                            }}
                            editable={['offer', 'price', 'cta', 'target_customer']}
                            loading={regenerating}
                            onRegenerate={handleRegenerate}
                            testIdPrefix="sp-tweaks-growth"
                          />

                          {/* Live Styled Preview (matches exported HTML/PDF) */}
                          {result.formatted_output.html_version && (
                            <StyledHtmlPreview
                              html={result.formatted_output.html_version}
                              title="Your Editable Template — Live Preview"
                              downloadName="SalesPage_Template.html"
                            />
                          )}

                          {/* Download HTML button */}
                          {result.formatted_output.html_version && (
                            <button
                              onClick={async () => {
                                const blob = new Blob([result.formatted_output.html_version], { type: 'text/html' });
                                await safeDownloadBlob(blob, 'SalesPage_Template.html');
                              }}
                              className="w-full px-4 py-2.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 text-sm font-medium rounded-lg flex items-center justify-center space-x-2 transition-colors"
                              data-testid="download-html-template-btn"
                            >
                              <ArrowDown size={14} />
                              <span>Download Editable HTML</span>
                            </button>
                          )}
                          {result.formatted_output.editing_guide && (
                            <p className="text-slate-500 text-xs">
                              {result.formatted_output.editing_guide}
                            </p>
                          )}

                          {/* Template Sections (text) */}
                          <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-white font-medium flex items-center space-x-2">
                                <PencilSimple size={16} className="text-blue-400" />
                                <span>Template Text</span>
                              </h5>
                              <button
                                onClick={() => handleCopy(JSON.stringify(result.formatted_output.template_sections, null, 2))}
                                className="px-3 py-1.5 bg-blue-500/20 hover:bg-blue-500/30 text-blue-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                              >
                                <Copy size={12} />
                                <span>{copied ? 'Copied!' : 'Copy'}</span>
                              </button>
                            </div>
                            <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                              {JSON.stringify(result.formatted_output.template_sections, null, 2)}
                            </pre>
                          </div>

                          {/* Headline Variations */}
                          {result.formatted_output.headline_variations?.length > 0 && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <h5 className="text-white font-medium mb-3">Headline Options</h5>
                              <div className="space-y-2">
                                {result.formatted_output.headline_variations.map((headline, i) => (
                                  <div key={i} className="flex items-center justify-between p-2 bg-black/20 rounded-lg">
                                    <span className="text-slate-300 text-sm">{headline}</span>
                                    <button
                                      onClick={() => handleCopy(headline)}
                                      className="p-1 hover:bg-white/10 rounded transition-colors"
                                    >
                                      <Copy size={12} className="text-slate-400" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      )}

                      {/* PRO OUTPUT */}
                      {result.formatted_output.output_type === 'done_for_you' && (
                        <>
                          {/* Download Buttons - HIDDEN for trial users */}
                          {!isTrial && (
                            <div className="flex space-x-3">
                              {/* Primary: Download PDF */}
                              <button
                                onClick={handleDownloadPDF}
                                disabled={!result?.has_pdf}
                                className="flex-1 py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="download-pdf-btn"
                              >
                                <Download size={18} weight="bold" />
                                <span>Download PDF</span>
                              </button>
                              
                              {/* Secondary: Download HTML */}
                              <button
                                onClick={handleDownloadHTML}
                                className="py-3 px-4 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl flex items-center justify-center space-x-2 transition-all"
                                data-testid="download-html-btn"
                              >
                                <Code size={16} />
                                <span>HTML</span>
                              </button>
                            </div>
                          )}
                          
                          {/* Trial user sees upgrade message instead of downloads */}
                          {isTrial && (
                            <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
                              <p className="text-red-400 font-medium mb-2">Downloads Locked</p>
                              <p className="text-slate-400 text-sm mb-3">Upgrade to Pro to download PDF and HTML files.</p>
                              <Link 
                                to="/pricing" 
                                className="inline-block px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                              >
                                Upgrade Now
                              </Link>
                            </div>
                          )}

                          {/* Live Styled Preview (matches exported HTML/PDF) */}
                          {result.formatted_output.html_version ? (
                            <>
                              <RegenerateWithTweaks
                                fields={{
                                  business_description: businessDescription,
                                  target_customer: targetCustomer,
                                  offer,
                                  price,
                                  cta,
                                }}
                                editable={['offer', 'price', 'cta', 'target_customer']}
                                loading={regenerating}
                                onRegenerate={handleRegenerate}
                                testIdPrefix="sp-tweaks-pro"
                              />
                              <StyledHtmlPreview
                                html={result.formatted_output.html_version}
                                title="Live Preview — Finished Sales Page"
                                downloadName="SalesPage.html"
                              />
                            </>
                          ) : (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
                              <div className="flex items-center justify-between mb-3">
                                <h5 className="text-white font-medium flex items-center space-x-2">
                                  <Eye size={16} className="text-purple-400" />
                                  <span>Preview</span>
                                </h5>
                                <button
                                  onClick={() => handleCopy(result.formatted_output.plain_text || '')}
                                  className="px-3 py-1.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 text-xs font-medium rounded-lg flex items-center space-x-1 transition-colors"
                                >
                                  <Copy size={12} />
                                  <span>Copy Text</span>
                                </button>
                              </div>
                              <pre className="text-slate-300 text-sm whitespace-pre-wrap font-sans bg-black/20 rounded-lg p-3 max-h-48 overflow-y-auto">
                                {result.formatted_output.plain_text || ''}
                              </pre>
                            </div>
                          )}

                          {/* Usage Instructions */}
                          {result.formatted_output.usage_instructions && (
                            <div className="flex items-center space-x-2 text-sm text-slate-400 bg-purple-500/10 border border-purple-500/20 rounded-lg p-3">
                              <CheckCircle size={16} className="text-purple-400" weight="fill" />
                              <span>{result.formatted_output.usage_instructions}</span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  )}

                  {/* Upgrade Prompt for Non-Pro Users */}
                  {result?.delivery_mode !== 'done_for_you' && effectiveTier !== 'pro' && (
                    <div className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 border border-purple-500/30 rounded-xl p-4" data-testid="upgrade-prompt">
                      <p className="text-white text-sm font-medium mb-2">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Want this finished and ready to use?'
                          : 'Want this fully built and ready to download?'
                        }
                      </p>
                      <p className="text-slate-400 text-xs mb-3">
                        {effectiveTier === 'starter' || effectiveTier === 'free' || effectiveTier === 'trial'
                          ? 'Upgrade to Pro to get done-for-you assets.'
                          : 'Upgrade to Pro.'
                        }
                      </p>
                      <Link
                        to="/pricing"
                        className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white text-sm font-medium rounded-lg transition-all"
                        data-testid="upgrade-btn"
                      >
                        <span>Upgrade</span>
                        <ArrowUpRight size={16} weight="bold" />
                      </Link>
                    </div>
                  )}

                  {/* Action Buttons — Sequential workflow routing */}
                  <div className="space-y-2 pt-2">
                    <button
                      onClick={() => { onAction?.('lead-magnet'); handleClose(); }}
                      className="w-full py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center space-x-2"
                      data-testid="next-lead-magnet-btn"
                    >
                      <span>Next: Create Lead Magnet</span>
                      <ArrowRight size={18} />
                    </button>
                    <div className="flex space-x-3">
                      <button
                        onClick={handleReset}
                        className="flex-1 py-3 bg-white/10 hover:bg-white/20 text-white font-medium rounded-xl transition-colors"
                      >
                        Create Another
                      </button>
                    </div>
                    <NextStepGuide
                      currentStep="sales_page"
                      onPick={(stepKey) => { handleReset(); onClose(); onPickStep?.(stepKey); }}
                      onDone={handleClose}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
      
      {/* Trial Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
          onClick={() => setShowUpgradePrompt(false)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 max-w-md w-full border border-purple-500/30"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
                <Lock size={32} weight="fill" className="text-white" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Upgrade to Continue</h3>
              <p className="text-slate-400 mb-6">{upgradeMessage}</p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold rounded-xl transition-all"
                >
                  View Plans
                </Link>
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="w-full py-3 bg-white/10 hover:bg-white/20 text-slate-300 font-medium rounded-xl transition-colors"
                >
                  Maybe Later
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
